<template>
  <div class="hyrox-admin-shell py-8 px-4">
    <v-container class="admin-container">
      <!-- Header -->
      <div class="d-flex flex-column flex-sm-row justify-space-between align-stretch align-sm-center mb-6">
        <div>
          <h1 class="text-h4 font-weight-black white--text italic-title">HYROX SCORE</h1>
          <h2 class="subtitle-1 yellow--text text--darken-2 font-weight-bold text-uppercase">Admin-Dashboard</h2>
        </div>

        <!-- Buttons stapeln sich nun auf Mobile vertikal und ziehen sich auf volle Breite -->
        <div class="d-flex flex-column flex-sm-row gap-3 mt-4 mt-sm-0">
          <v-btn color="#fbc02d" class="black--text font-weight-bold" @click="openManualRegDialog">
            <v-icon left>mdi-plus</v-icon> Anmeldung hinzufügen
          </v-btn>
          <v-btn color="grey darken-3" class="white--text font-weight-bold" @click="exportToExcel" :loading="exporting">
            <v-icon left>mdi-microsoft-excel</v-icon> Excel Export
          </v-btn>
          <v-btn text outlined color="grey lighten-1" @click="fetchData"> <v-icon left>mdi-refresh</v-icon> Aktualisieren </v-btn>
        </div>
      </div>

      <!-- Stats Cards -->
      <v-row class="mb-6">
        <v-col cols="12" sm="6" md="3">
          <v-card dark class="stat-card pa-4 rounded-xl">
            <div class="caption grey--text">Aktive Teams (Teilnehmer)</div>
            <div class="text-h4 font-weight-black white--text my-1">
              {{ stats.activeTeams }} <span class="text-subtitle-1 grey--text font-weight-medium">({{ stats.activeParticipants }} Personen)</span>
            </div>
            <v-progress-linear :value="(stats.activeParticipants / stats.limit) * 100" color="#fbc02d" class="mt-2 rounded"></v-progress-linear>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card dark class="stat-card pa-4 rounded-xl">
            <div class="caption grey--text">Teilnehmer-Limit</div>
            <div class="text-h4 font-weight-black white--text my-1">{{ stats.limit }} Plätze</div>
            <div class="caption grey--text mt-2">Kapazität für das Event</div>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card dark class="stat-card pa-4 rounded-xl">
            <div class="caption grey--text">Einnahmen (Bezahlt)</div>
            <div class="text-h4 font-weight-black green--text text--accent-3 my-1">CHF {{ stats.totalPaidRevenue.toFixed(2) }}</div>
            <div class="caption grey--text mt-2">Bestätigte Zahlungen</div>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card dark class="stat-card pa-4 rounded-xl">
            <div class="caption grey--text">Freie Plätze</div>
            <div class="text-h4 font-weight-black yellow--text text--darken-2 my-1">
              {{ Math.max(0, stats.limit - stats.activeParticipants) }}
            </div>
            <div class="caption grey--text mt-2">von max. {{ stats.limit }} Plätzen</div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Event Settings Card -->
      <v-card dark class="stat-card pa-5 rounded-xl mb-6">
        <h3 class="subtitle-1 font-weight-bold white--text mb-4 d-flex align-center">
          <v-icon color="#fbc02d" class="mr-2">mdi-cog-outline</v-icon>
          Event-Einstellungen
        </h3>
        <v-row align="center">
          <v-col cols="12" md="3">
            <div class="caption grey--text mb-2">Teilnehmer-Limit</div>
            <v-btn-toggle v-model="limitToggleIndex" mandatory dense class="limit-toggle rounded-lg">
              <v-btn value="84" class="px-4">84 Plätze</v-btn>
              <v-btn value="126" class="px-4">126 Plätze</v-btn>
            </v-btn-toggle>
          </v-col>

          <v-col cols="12" sm="6" md="3" v-if="isEditMode">
            <div class="caption grey--text mb-1">Course ID Frauenfeld (5k Run)</div>
            <v-text-field
              v-model="settings.courseFrauenfeldId"
              label="Course Execution ID Frauenfeld"
              outlined
              dense
              hide-details
              class="search-field rounded-lg"
              placeholder="z.B. 60fb1a..."></v-text-field>
          </v-col>

          <v-col cols="12" sm="6" md="3" v-if="isEditMode">
            <div class="caption grey--text mb-1">Course ID St. Gallen (5k Run)</div>
            <v-text-field
              v-model="settings.courseStGallenId"
              label="Course Execution ID St. Gallen"
              outlined
              dense
              hide-details
              class="search-field rounded-lg"
              placeholder="z.B. 60fb1b..."></v-text-field>
          </v-col>

          <v-col cols="12" sm="6" md="3">
            <div class="caption grey--text mb-1">Anmeldefrist (Deadline)</div>
            <v-text-field
              v-model="settings.deadlineDate"
              type="datetime-local"
              outlined
              dense
              hide-details
              class="search-field rounded-lg"></v-text-field>
          </v-col>

          <v-col cols="12" class="text-right pt-2">
            <v-btn color="#fbc02d" class="black--text font-weight-bold rounded-lg" :loading="savingSettings" @click="saveAdminSettings">
              Einstellungen Speichern
            </v-btn>
          </v-col>
        </v-row>
      </v-card>

      <!-- Registrations Table -->
      <v-card dark class="table-card rounded-xl pa-4">
        <!-- Search and Filters -->
        <v-row class="mb-4 align-center" dense>
          <v-col cols="12" sm="6" md="4">
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              label="Suchen..."
              single-line
              hide-details
              outlined
              dense
              class="search-field rounded-lg"></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="8" class="text-sm-right mt-3 mt-sm-0">
            <v-chip-group v-model="filterStatus" mandatory active-class="active-chip">
              <v-chip filter value="all" small outlined>Alle ({{ registrations.length }})</v-chip>
              <v-chip filter value="pending" small color="orange" outlined>Zahlung offen ({{ countStatus("pending_approval") }})</v-chip>
              <v-chip filter value="paid" small color="green" outlined>Bezahlt ({{ countStatus("paid") }})</v-chip>
              <v-chip filter value="unpaid" small color="red" outlined>Unbezahlt ({{ countStatus("unpaid") }})</v-chip>
              <v-chip filter value="cancelled" small color="grey" outlined>Storniert ({{ countCancelled() }})</v-chip>
            </v-chip-group>
          </v-col>
        </v-row>

        <!-- Data Table -->
        <v-data-table
          :headers="headers"
          :items="filteredRegistrations"
          :search="search"
          :loading="loading"
          class="custom-table"
          :items-per-page="15"
          dark>
          <!-- Date Format -->
          <template v-slot:item.registeredAt="{ item }">
            <span class="caption">{{ formatDate(item.registeredAt) }}</span>
          </template>

          <!-- Main User Column -->
          <template v-slot:item.user="{ item }">
            <div class="py-1">
              <div class="body-2 font-weight-bold white--text">
                {{ item.user ? `${item.user.vorname} ${item.user.nachname}` : "Gelöschter User" }}
              </div>
              <div class="caption grey--text">{{ item.user ? item.user.email : "" }}</div>
              <v-chip x-small v-if="userHasInstructorTag(item)" color="green" class="black--text font-weight-black mt-1">INSTRUCTOR</v-chip>
            </div>
          </template>

          <!-- Partner Column -->
          <template v-slot:item.partner="{ item }">
            <div v-if="item.partner && item.partner.email" class="py-1">
              <div class="body-2 font-weight-bold white--text">{{ item.partner.firstName }} {{ item.partner.lastName }}</div>
              <div class="caption grey--text">{{ item.partner.email }}</div>
              <div class="d-flex gap-1 mt-1">
                <v-chip x-small outlined color="grey lighten-1">MEMBER</v-chip>
                <v-chip x-small v-if="item.partner.isInstructor" color="green" class="black--text font-weight-black">INSTRUCTOR</v-chip>
              </div>
            </div>
            <div v-else class="py-1">
              <v-chip x-small outlined color="grey">Einzelstarter/in (zufälliger Judge)</v-chip>
            </div>
          </template>

          <!-- Price Column -->
          <template v-slot:item.totalFee="{ item }">
            <span class="font-weight-bold" :class="item.totalFee === 0 ? 'green--text' : 'white--text'"> CHF {{ item.totalFee.toFixed(2) }} </span>
          </template>

          <!-- Payment Status Column -->
          <template v-slot:item.paymentStatus="{ item }">
            <v-chip x-small :color="getPaymentColor(item.paymentStatus)" class="font-weight-bold black--text" label>
              {{ getPaymentText(item.paymentStatus) }}
            </v-chip>
          </template>

          <!-- Registration Status Column -->
          <template v-slot:item.status="{ item }">
            <v-chip x-small :color="item.status === 'active' ? 'green' : 'grey'" outlined class="font-weight-bold">
              {{ item.status === "active" ? "Aktiv" : "Storniert" }}
            </v-chip>
          </template>

          <!-- Actions -->
          <template v-slot:item.actions="{ item }">
            <v-menu offset-y dark>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon v-bind="attrs" v-on="on">
                  <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-list dense bg-color="#1c1c1e">
                <!-- Payment updates -->
                <v-list-item @click="updateStatus(item._id, { paymentStatus: 'paid' })">
                  <v-list-item-icon class="mr-2">
                    <v-icon color="green" small>mdi-cash-check</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>Als bezahlt markieren</v-list-item-title>
                </v-list-item>

                <v-list-item @click="updateStatus(item._id, { paymentStatus: 'unpaid' })">
                  <v-list-item-icon class="mr-2">
                    <v-icon color="red" small>mdi-cash-remove</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>Als unbezahlt markieren</v-list-item-title>
                </v-list-item>

                <v-divider class="grey darken-3"></v-divider>

                <!-- Registration status updates -->
                <v-list-item @click="updateStatus(item._id, { status: item.status === 'active' ? 'cancelled' : 'active' })">
                  <v-list-item-icon class="mr-2">
                    <v-icon color="orange" small>
                      {{ item.status === "active" ? "mdi-close-circle" : "mdi-checkbox-marked-circle" }}
                    </v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>
                    {{ item.status === "active" ? "Anmeldung stornieren" : "Anmeldung reaktivieren" }}
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>
        </v-data-table>
      </v-card>
    </v-container>

    <!-- Dialog for Manual Registration -->
    <v-dialog v-model="manualRegDialog.show" max-width="500px" persistent class="rounded-xl">
      <v-card dark class="pa-4 rounded-xl border-highlight" style="background-color: #1c1c1e; border: 1px solid rgba(255, 255, 255, 0.08)">
        <div class="d-flex align-center justify-space-between mb-4">
          <h2 class="text-h5 font-weight-black white--text italic-title">
            <v-icon color="#fbc02d" class="mr-2">mdi-account-plus</v-icon>
            Manuelle Anmeldung
          </h2>
          <v-btn icon color="grey" @click="manualRegDialog.show = false" :disabled="manualRegDialog.saving">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="mb-4">
          <p class="caption grey--text">Suche nach einem Mitglied, das manuell und kostenlos angemeldet werden soll (Instruktorenanmeldung).</p>
        </div>

        <!-- User Search Component -->
        <div class="mb-4">
          <user-search
            class="pa-0 search-field rounded-lg"
            rights="mk_hyroxPFT"
            label="Mitglied suchen (Name oder E-Mail)..."
            @update="onUserSelected"></user-search>
        </div>

        <!-- Selected User details -->
        <v-slide-y-transition>
          <div v-if="manualRegDialog.loading" class="text-center py-4">
            <v-progress-circular indeterminate color="#fbc02d" size="24"></v-progress-circular>
            <div class="caption grey--text mt-1">Mitgliedsdetails werden geladen...</div>
          </div>
          <div
            v-else-if="manualRegDialog.selectedUser"
            class="mb-4 pa-3 rounded-lg"
            style="background-color: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.05)">
            <div class="caption yellow--text text--darken-2 font-weight-black">Ausgewähltes Mitglied:</div>
            <div class="body-1 font-weight-bold white--text">
              {{ manualRegDialog.selectedUser.vorname }} {{ manualRegDialog.selectedUser.nachname }}
            </div>
            <div class="body-2 grey--text">{{ manualRegDialog.selectedUser.email }}</div>
          </div>
        </v-slide-y-transition>

        <!-- Partner registration (Optional) -->
        <v-slide-y-transition>
          <div v-if="manualRegDialog.selectedUser">
            <v-divider class="my-4 grey darken-3"></v-divider>

            <h3 class="subtitle-2 font-weight-black yellow--text text--darken-2 text-uppercase mb-3">
              Partner/in eintragen (gegenseitiger Judge - Optional)
            </h3>

            <v-row dense>
              <v-col cols="6">
                <v-text-field
                  v-model="manualRegDialog.partner.firstName"
                  label="Vorname Partner/in"
                  outlined
                  dense
                  class="rounded-lg search-field"></v-text-field>
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="manualRegDialog.partner.lastName"
                  label="Nachname Partner/in"
                  outlined
                  dense
                  class="rounded-lg search-field"></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="manualRegDialog.partner.email"
                  label="E-Mail Partner/in"
                  outlined
                  dense
                  class="rounded-lg search-field"></v-text-field>
              </v-col>
            </v-row>
          </div>
        </v-slide-y-transition>

        <!-- Actions -->
        <div class="d-flex justify-end gap-2 mt-4">
          <v-btn text color="grey" @click="manualRegDialog.show = false" :disabled="manualRegDialog.saving"> Abbrechen </v-btn>
          <v-btn
            color="#fbc02d"
            class="black--text font-weight-black rounded-lg"
            :disabled="!manualRegDialog.selectedUser"
            :loading="manualRegDialog.saving"
            @click="submitManualRegistration">
            Kostenlos anmelden
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from "@/api"
import userSearch from "@/components/userSearch.vue"

export default {
  name: "HyroxScoreAdmin",
  components: {
    userSearch,
  },
  data() {
    return {
      loading: true,
      exporting: false,
      search: "",
      filterStatus: "all",
      limitToggleIndex: "84",

      registrations: [],
      stats: {
        activeParticipants: 0,
        activeTeams: 0,
        totalPaidRevenue: 0,
        limit: 84,
      },

      settings: {
        courseFrauenfeldId: "",
        courseStGallenId: "",
        deadlineDate: "",
      },
      savingSettings: false,

      manualRegDialog: {
        show: false,
        loading: false,
        saving: false,
        selectedUserId: null,
        selectedUser: null,
        partner: {
          firstName: "",
          lastName: "",
          email: "",
        },
      },

      headers: [
        { text: "Datum", value: "registeredAt", width: "90px" },
        { text: "Starter 1 (Anmelder)", value: "user", sortable: false },
        { text: "Starter 2 (Partner/Judge)", value: "partner", sortable: false },
        { text: "Gebühr", value: "totalFee", align: "right", width: "100px" },
        { text: "Zahlung", value: "paymentStatus", align: "center", width: "110px" },
        { text: "Status", value: "status", align: "center", width: "100px" },
        { text: "", value: "actions", sortable: false, align: "right", width: "60px" },
      ],
    }
  },
  computed: {
    isEditMode() {
      return this.$route.query.hasOwnProperty("edit")
    },
    filteredRegistrations() {
      if (this.filterStatus === "all") return this.registrations
      if (this.filterStatus === "cancelled") return this.registrations.filter((r) => r.status === "cancelled")

      // Filter by paymentStatus for active registrations
      const active = this.registrations.filter((r) => r.status === "active")
      if (this.filterStatus === "pending") return active.filter((r) => r.paymentStatus === "pending_approval")
      if (this.filterStatus === "paid") return active.filter((r) => r.paymentStatus === "paid")
      if (this.filterStatus === "unpaid") return active.filter((r) => r.paymentStatus === "unpaid")

      return this.registrations
    },
  },
  async created() {
    await this.fetchData()
  },
  methods: {
    formatDate(dateStr) {
      if (!dateStr) return ""
      return new Date(dateStr).toLocaleDateString("de-CH", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      })
    },
    formatDateTimeLocal(dateVal) {
      if (!dateVal) return ""
      const d = new Date(dateVal)
      if (isNaN(d.getTime())) return ""

      const year = d.getFullYear()
      const month = String(d.getMonth() + 1).padStart(2, "0")
      const day = String(d.getDate()).padStart(2, "0")
      const hours = String(d.getHours()).padStart(2, "0")
      const minutes = String(d.getMinutes()).padStart(2, "0")

      return `${year}-${month}-${day}T${hours}:${minutes}`
    },
    userHasInstructorTag(item) {
      return item.totalFee === 0 || (item.totalFee === 20 && item.partner && item.partner.isInstructor === true)
    },
    getPaymentColor(status) {
      if (status === "paid") return "green accent-3"
      if (status === "pending_approval") return "orange"
      return "red lighten-1"
    },
    getPaymentText(status) {
      if (status === "paid") return "Bezahlt"
      if (status === "pending_approval") return "Freigabe"
      return "Offen"
    },
    countStatus(paymentStatus) {
      return this.registrations.filter((r) => r.status === "active" && r.paymentStatus === paymentStatus).length
    },
    countCancelled() {
      return this.registrations.filter((r) => r.status === "cancelled").length
    },
    async fetchData() {
      this.loading = true
      try {
        const res = await api.hyroxScore.admin.fetchRegistrations()
        this.registrations = res.registrations || []
        this.stats = res.stats || {
          activeParticipants: 0,
          activeTeams: 0,
          totalPaidRevenue: 0,
          limit: 84,
        }
        this.limitToggleIndex = String(this.stats.limit)

        if (res.settings) {
          this.settings.courseFrauenfeldId = res.settings.courseFrauenfeldId || ""
          this.settings.courseStGallenId = res.settings.courseStGallenId || ""
          this.settings.deadlineDate = res.settings.deadlineDate ? this.formatDateTimeLocal(res.settings.deadlineDate) : ""
        }
      } catch (e) {
        this.$toast.error("Registrierungen konnten nicht geladen werden.")
        console.error(e)
      } finally {
        this.loading = false
      }
    },
    async saveAdminSettings() {
      this.savingSettings = true
      try {
        await api.hyroxScore.admin.saveSettings({
          limit: Number(this.limitToggleIndex),
          courseFrauenfeldId: this.settings.courseFrauenfeldId,
          courseStGallenId: this.settings.courseStGallenId,
          deadlineDate: this.settings.deadlineDate,
        })
        this.$toast.success("Einstellungen wurden erfolgreich gespeichert.")
        await this.fetchData()
      } catch (e) {
        console.error(e)
        this.$toast.error("Speichern der Einstellungen fehlgeschlagen.")
      } finally {
        this.savingSettings = false
      }
    },
    async changeLimit(limit) {
      this.limitToggleIndex = String(limit)
      await this.saveAdminSettings()
    },
    openManualRegDialog() {
      this.manualRegDialog = {
        show: true,
        loading: false,
        saving: false,
        selectedUserId: null,
        selectedUser: null,
        partner: {
          firstName: "",
          lastName: "",
          email: "",
        },
      }
    },
    async onUserSelected(userId) {
      if (!userId) {
        this.manualRegDialog.selectedUserId = null
        this.manualRegDialog.selectedUser = null
        return
      }

      this.manualRegDialog.selectedUserId = userId
      this.manualRegDialog.loading = true
      try {
        const userDetails = await api.hyroxScore.admin.fetchUserDetails(userId)
        this.manualRegDialog.selectedUser = userDetails
      } catch (e) {
        console.error(e)
        this.$toast.error("Benutzerdetails konnten nicht geladen werden.")
        this.manualRegDialog.selectedUserId = null
        this.manualRegDialog.selectedUser = null
      } finally {
        this.manualRegDialog.loading = false
      }
    },
    async submitManualRegistration() {
      if (!this.manualRegDialog.selectedUserId) {
        this.$toast.error("Bitte wähle zuerst einen Hauptbenutzer aus.")
        return
      }

      this.manualRegDialog.saving = true
      try {
        const payload = {
          userId: this.manualRegDialog.selectedUserId,
          partnerFirstName: this.manualRegDialog.partner.firstName,
          partnerLastName: this.manualRegDialog.partner.lastName,
          partnerEmail: this.manualRegDialog.partner.email,
        }

        await api.hyroxScore.admin.manualRegister(payload)
        this.$toast.success("Manuelle Anmeldung erfolgreich hinzugefügt.")
        this.manualRegDialog.show = false
        await this.fetchData()
      } catch (error) {
        console.error(error)
        this.$toast.error(error.response?.data || "Manuelle Anmeldung fehlgeschlagen.")
      } finally {
        this.manualRegDialog.saving = false
      }
    },
    async updateStatus(registrationId, updatePayload) {
      try {
        await api.hyroxScore.admin.updateStatus(registrationId, updatePayload)
        this.$toast.success("Status erfolgreich aktualisiert.")
        await this.fetchData()
      } catch (e) {
        this.$toast.error("Statusaktualisierung fehlgeschlagen.")
      }
    },
    exportToExcel() {
      this.exporting = true
      try {
        const downloadUrl = api.hyroxScore.admin.exportCsvUrl()
        const token = localStorage.getItem("token")
        const axios = require("axios")
        axios({
          url: downloadUrl,
          method: "GET",
          headers: { Authorization: token },
          responseType: "blob",
        })
          .then((response) => {
            const blob = new Blob([response.data], { type: "text/csv;charset=utf-8;\uFEFF" })
            const link = document.createElement("a")
            link.href = URL.createObjectURL(blob)
            link.setAttribute("download", "hyrox_score_registrations.csv")
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
          })
          .catch(() => {
            this.$toast.error("Export fehlgeschlagen.")
          })
          .finally(() => {
            this.exporting = false
          })
      } catch (error) {
        this.exporting = false
        this.$toast.error("Export fehlgeschlagen.")
      }
    },
  },
}
</script>

<style scoped>
.hyrox-admin-shell {
  background-color: #0f0f0f;
  min-height: 100vh;
  color: white;
}

.admin-container {
  max-width: 1200px;
  margin: 0 auto;
}

.italic-title {
  font-style: italic;
  font-weight: 950;
  letter-spacing: -1px;
}

/* Cards */
.stat-card {
  background-color: #1c1c1e !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3) !important;
}

.limit-toggle {
  background-color: rgba(255, 255, 255, 0.05) !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.limit-toggle .v-btn {
  color: #777777 !important;
  background-color: transparent !important;
}

.limit-toggle .v-btn--active {
  color: #000000 !important;
  background-color: #fbc02d !important;
}

/* Table Card */
.table-card {
  background-color: #1c1c1e !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
}

.search-field :deep(.v-input__control) {
  border-radius: 8px !important;
}

.active-chip {
  background-color: rgba(251, 192, 45, 0.1) !important;
  color: #fbc02d !important;
  border-color: #fbc02d !important;
}

/* Custom Table Styles */
.custom-table :deep(table) {
  background-color: transparent !important;
}

.custom-table :deep(thead th) {
  font-weight: bold !important;
  text-transform: uppercase;
  font-size: 11px !important;
  letter-spacing: 0.5px;
  color: #777777 !important;
  border-bottom: 2px solid rgba(255, 255, 255, 0.08) !important;
}

.custom-table :deep(tbody td) {
  border-bottom: 1px solid rgba(255, 255, 255, 0.04) !important;
}

.custom-table :deep(tbody tr:hover) {
  background-color: rgba(255, 255, 255, 0.02) !important;
}

.gap-1 {
  gap: 4px;
}
.gap-3 {
  gap: 12px;
}
</style>
